import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { Dayjs } from "dayjs";
import { Button, DatePicker, Form, Input, Modal, Select, Space, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatDateTime } from "@/utils/dateTime";
import { listShopOrders, exportShopOrders, getShopOrderSummary } from "@/api/shopOrder";
import type { ShopOrderQuery, ShopOrderRow, ShopOrderSummary } from "@/api/shopOrder";
import type { MallOrderProductType } from "./types";
import { OrderDetailPanel } from "./OrderDetailPanel";
import { OrderOperationModal } from "./OrderOperationModal";
import { OrderRemarkModal } from "./OrderRemarkModal";
import type { OrderOperation } from "./orderOperations";
import styles from "../product/ProductDialog.module.css";

interface OrderProps { mode?: MallOrderProductType | "all" }
interface Filters {
  orderNo?: string; buyerKeyword?: string; goodsName?: string;
  orderStatus?: number; goodsType?: number; merchantName?: string;
  range?: [Dayjs, Dayjs];
  finishRange?: [Dayjs, Dayjs];
}
const statuses = ["待支付", "待发货", "待收货", "已完成", "已取消", "用户申请退款", "退款中", "已退款", "不予退款", "待自提"];
const statusColors = ["gold", "orange", "blue", "green", "default", "magenta", "purple", "cyan", "red", "blue"];
const renderStatus = (row: ShopOrderRow): ReactElement => <Tag color={statusColors[row.orderStatus] ?? "default"}>{row.orderStatusDesc || statuses[row.orderStatus] || "未知"}</Tag>;
const money = (value?: number | null): string => value == null ? "-" : `¥${Number(value).toFixed(2)}`;
const columns: ColumnsType<ShopOrderRow> = [
  { title: "订单号", dataIndex: "orderNo", width: 180 },
  { title: "下单用户", width: 160, render: (_, row): ReactElement => <div>{row.nickName || row.appUserId}<div>{row.mobile}</div></div> },
  { title: "商品", dataIndex: "goodsName", width: 180 },
  { title: "商品规格", dataIndex: "skuName", width: 130 },
  { title: "商品类型", width: 110, render: (_, row): ReactElement => <Tag color={row.goodsType === 1 ? "blue" : row.goodsType === 2 ? "purple" : "default"}>{row.goodsType === 1 ? "自营" : row.goodsType === 2 ? "入驻商家" : "-"}</Tag> },
  { title: "商家名称", dataIndex: "merchantName", width: 140 },
  { title: "自营供应商", dataIndex: "selfSupplier", width: 140 },
  { title: "购买数量", dataIndex: "goodsNum", width: 100 },
  { title: "售价", dataIndex: "goodsPrice", render: money, width: 100 },
  { title: "订单金额", dataIndex: "totalAmount", render: money, width: 110 },
  { title: "用户实付", dataIndex: "payAmount", render: money, width: 110 },
  { title: "结算价", dataIndex: "settlementAmount", render: money, width: 110 },
  { title: "平台分润金额", dataIndex: "platformCommissionAmount", render: money, width: 120 },
  { title: "订单状态", width: 140, render: (_, row): ReactElement => renderStatus(row) },
  { title: "收货地址", dataIndex: "receiveAddress", width: 240 },
  { title: "下单时间", dataIndex: "createTime", render: formatDateTime, width: 170 },
  { title: "订单完成时间", dataIndex: "finishTime", render: formatDateTime, width: 170 },
];
export function orderQuery(values: Filters, mode: OrderProps["mode"], pageSize: number): ShopOrderQuery {
  const { range, finishRange, ...fields } = values;
  return {
    ...fields,
    goodsType: mode === "settledMerchant" ? 2 : mode === "selfOperated" ? 1 : fields.goodsType,
    startTime: range?.[0]?.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    endTime: range?.[1]?.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    finishStartTime: finishRange?.[0]?.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    finishEndTime: finishRange?.[1]?.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    ypageNum: 1, ypageSize: pageSize,
  };
}
function Order({ mode = "all" }: OrderProps): ReactElement {
  const [form] = Form.useForm<Filters>();
  const [query, setQuery] = useState<ShopOrderQuery>({ ypageNum: 1, ypageSize: 10 });
  const [rows, setRows] = useState<ShopOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<ShopOrderSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState<ShopOrderRow | null>(null);
  const [remarkOrder, setRemarkOrder] = useState<ShopOrderRow | null>(null);
  const handleCloseRemark = (): void => setRemarkOrder(null);
  const [operation, setOperation] = useState<{ row: ShopOrderRow; action: OrderOperation } | null>(null);
  const [revision, setRevision] = useState(0);
  const handleCloseDetail = (): void => setDetail(null);
  const handleOperation = (row: ShopOrderRow, action: OrderOperation): void => setOperation({ row, action });
  const handleCloseOperation = (): void => setOperation(null);
  const handleOperationSuccess = (): void => {
    setOperation(null);
    setRevision((value) => value + 1);
  };
  const tableColumns: ColumnsType<ShopOrderRow> = [
    ...columns,
    { title: "操作", key: "actions", fixed: "right", width: 230,
      render: (_, row): ReactElement => <Space>
        <Button size="small" onClick={() => setDetail(row)}>详情</Button>
        {row.goodsType === 1 && row.orderStatus === 1 &&
          <Button size="small" type="primary" onClick={() => handleOperation(row, "deliver")}>发货</Button>}
        {row.goodsType === 1 && [5, 6].includes(row.orderStatus) &&
          <Button size="small" onClick={() => setDetail(row)}>处理售后</Button>}
        <Button size="small" onClick={() => setRemarkOrder(row)}>备注</Button>
      </Space> },
  ];
  const goodsType = mode === "settledMerchant" ? 2 : mode === "selfOperated" ? 1 : query.goodsType;
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setSummary(null);
    getShopOrderSummary({ ...query, goodsType }, controller.signal).then((result) => {
      if (!controller.signal.aborted) setSummary(result.data);
    }).catch(() => {
      // 请求层统一提示失败，统计值保持为空，避免显示上次查询的结果。
    });
    listShopOrders({ ...query, goodsType }, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      setRows(result.rows ?? []);
      setTotal(result.total ?? 0);
    }).catch(() => {
      if (controller.signal.aborted) return;
      setRows([]);
      setTotal(0);
      // 请求层统一提示失败，不在页面展示技术错误；查询按钮可重新加载。
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return (): void => controller.abort();
  }, [query, goodsType, revision]);
  const handleSearch = (values: Filters): void => setQuery(orderQuery(values, mode, query.ypageSize));
  const handleReset = (): void => {
    form.resetFields();
    setQuery(orderQuery({}, mode, query.ypageSize));
  };
  const handlePageChange = (page: number, pageSize: number): void => {
    setQuery({ ...query, ypageNum: pageSize === query.ypageSize ? page : 1, ypageSize: pageSize });
  };
  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const blob = await exportShopOrders({ ...query, goodsType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "商城订单.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error("导出失败，请稍后重试");
    } finally { setExporting(false); }
  };
  return <div>
    <Form form={form} layout="inline" onFinish={handleSearch} style={{ gap: 16, marginBottom: 20 }}>
      <Form.Item label="订单号" name="orderNo"><Input allowClear placeholder="请输入订单号" style={{ width: 160 }} /></Form.Item>
      <Form.Item label="下单用户" name="buyerKeyword"><Input allowClear placeholder="ID / 昵称 / 手机号" style={{ width: 180 }} /></Form.Item>
      <Form.Item label="商品名称" name="goodsName"><Input allowClear placeholder="请输入商品名称" style={{ width: 160 }} /></Form.Item>
      <Form.Item label="订单状态" name="orderStatus"><Select allowClear placeholder="请选择订单状态" style={{ width: 170 }} options={statuses.map((label, value) => ({ label, value }))} /></Form.Item>
      {mode === "all" && <Form.Item label="商品类型" name="goodsType"><Select allowClear placeholder="请选择商品类型" style={{ width: 140 }} options={[{ label: "自营", value: 1 }, { label: "入驻商家", value: 2 }]} /></Form.Item>}
      <Form.Item label="商家名称" name="merchantName"><Input allowClear placeholder="请输入商家名称" style={{ width: 160 }} /></Form.Item>
      <Form.Item label="下单时间" name="range"><DatePicker.RangePicker placeholder={["开始日期", "结束日期"]} /></Form.Item>
      <Form.Item label="完成订单时间" name="finishRange"><DatePicker.RangePicker placeholder={["开始日期", "结束日期"]} /></Form.Item>
      <Space><Button type="primary" htmlType="submit" loading={loading}>查询</Button><Button onClick={handleReset}>重置</Button><Button onClick={handleExport} loading={exporting}>导出</Button></Space>
    </Form>
    <Space wrap size={32} style={{ marginBottom: 16 }}>
      <span>购买数量合计：<strong>{summary?.totalBuyNum ?? "-"}</strong></span>
      <span>销售金额合计：<strong>{money(summary?.totalSaleAmount)}</strong></span>
      <span>结算金额合计：<strong>{money(summary?.settlementAmount)}</strong></span>
    </Space>
    <Table<ShopOrderRow> bordered size="small" loading={loading}
      rowKey={(row): string => `${row.id}-${row.itemId ?? "order"}`}
      columns={tableColumns}
      dataSource={rows} scroll={{ x: "max-content" }}
      pagination={{ current: query.ypageNum, pageSize: query.ypageSize, total, showSizeChanger: true, onChange: handlePageChange }} />
    <Modal title="订单详情" className={styles.modal} open={detail !== null} onCancel={handleCloseDetail} footer={null} width={1000} destroyOnClose>
      {detail && <OrderDetailPanel key={detail.id} row={detail} revision={revision} onOperate={handleOperation} />}
    </Modal>
    {remarkOrder && <OrderRemarkModal key={remarkOrder.id} row={remarkOrder}
      onCancel={handleCloseRemark} onSuccess={handleCloseRemark} />}
    {operation && <OrderOperationModal key={`${operation.row.id}-${operation.action}`}
      row={operation.row} action={operation.action} onCancel={handleCloseOperation} onSuccess={handleOperationSuccess} />}
  </div>;
}
export default Order;
